/**
 * Hook for managing table sorting state and logic
 */

import { useCallback, useMemo, useState } from 'react';
import type {
  RateShopDataRow,
  RateShopHeader,
  SortConfig,
  SortDirection,
} from '../types/rate-shop-types';

interface UseTableSortingProps {
  data: RateShopDataRow[];
  headers: RateShopHeader[];
}

interface UseTableSortingReturn {
  sortedData: RateShopDataRow[];
  sortConfig: SortConfig;
  handleSort: (dataKey: string) => void;
  getSortIcon: (dataKey: string) => 'asc' | 'desc' | null;
}

export function useTableSorting({ data, headers }: UseTableSortingProps): UseTableSortingReturn {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: null,
    direction: null,
  });

  const sortedData = useMemo(() => {
    if (!sortConfig.column || !sortConfig.direction) return data;

    const columnIndex = headers.findIndex((header) => header.dataKey === sortConfig.column);
    if (columnIndex === -1) return data;

    return [...data].sort((a, b) => {
      const aCellObj = a[columnIndex];
      const bCellObj = b[columnIndex];

      let aVal = aCellObj?.value;
      let bVal = bCellObj?.value;

      // Handle date sorting (stay_date column)
      if (aCellObj?.dataKey === 'stay_date') {
        aVal = aCellObj?.stayDate;
        bVal = bCellObj?.stayDate;
      }

      // Handle null values
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bVal === null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Handle numeric sorting for rates
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle string sorting
      if (sortConfig.direction === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  }, [data, headers, sortConfig]);

  const handleSort = useCallback((dataKey: string) => {
    setSortConfig((current) => {
      if (current.column === dataKey) {
        if (current.direction === 'asc') {
          return { column: dataKey, direction: 'desc' };
        } else if (current.direction === 'desc') {
          return { column: null, direction: null };
        } else {
          return { column: dataKey, direction: 'asc' };
        }
      } else {
        return { column: dataKey, direction: 'asc' };
      }
    });
  }, []);

  const getSortIcon = useCallback(
    (dataKey: string): SortDirection => {
      if (sortConfig.column !== dataKey) return null;
      return sortConfig.direction;
    },
    [sortConfig],
  );

  return {
    sortedData,
    sortConfig,
    handleSort,
    getSortIcon,
  };
}
